import React, { ReactElement, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { AuthenticationApiResponse, AuthenticationResultCode } from 'features/auth';
import { Page, Box, Typography, Flex, HelperText, Loading } from 'components';
import { request, createUrl, useFormikHandles } from 'app/utils';
import { useSchool } from 'features/school';
import Alert, { useAlert } from 'features/alert';
import { AuthButton, AuthInput2 } from 'features/auth/components';
import { AxiosError } from 'axios';
import { useEducational } from 'features/educational';
import { EducationalInstitutionRecoverUserByActivationCodeResponse } from 'features/educational/types/EducationalInstitutionRecoverUserByActivationCodeResponse';
import usestorage from 'app/utils/storage';

const ForgotPasswordView = (): ReactElement => {
  const { apiGatewayUrl, tenantUuid, schoolUuid } = useSchool();
  const { setAlert, clearAlert } = useAlert();
  const navigate = useNavigate();
  const storage = usestorage('sessionStorage');

  const [withPin, setWithPin] = React.useState(false);

  const { setActivatedCode } = useEducational();

  const initialValues: Record<string, any> = {
    login: '',
    pin: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      // email: Yup.string().email('O e-mail está inválido').required('O e-mail é obrigatório'),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const { login, pin } = values;

      request<AuthenticationApiResponse>(
        {
          url: withPin && pin ? '/api/v1/School/EducationalInstitution/TryRecoverPassword' : '/api/v1/User/PasswordRecovery',
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            login,
            ...(withPin && { pin }),
            tenantUuid,
            schoolUuid,
          },
        },
        true
      )
        .then(({ data }) => {
          const message = data.message || 'O login informado não foi encontrado.';

          if (withPin) {
            const recoverPasswordDsata = data as EducationalInstitutionRecoverUserByActivationCodeResponse;
            setActivatedCode({
              activatedUserGeneratedLogin: recoverPasswordDsata.login,
              activatedUserGeneratedRecoveryToken: recoverPasswordDsata.recoveryToken,
            });

            navigate(createUrl('educational/reset-password'));
          }

          setWithPin(data.authenticationResult === AuthenticationResultCode.RecoveryByEmailFailed);

          if (data.authenticationResult === AuthenticationResultCode.Success) {
            setAlert({ type: 'success', message });
            resetForm();
          }
        })
        .catch((error: AxiosError<AuthenticationApiResponse>) => {
          const message = error.response?.data?.message || 'Ocorreu um erro ao recuperar a senha.';

          setSubmitting(false);
          setAlert({
            type: 'error',
            message,
          });
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  const userWithPin = storage.get('userWithPin');
  const updateUserWithPin = () => {
    setWithPin(true);
    formik.setFieldValue('login', userWithPin, true);
    storage.remove('userWithPin');
  };

  useEffect(() => () => clearAlert(), []); // Clear Alert

  useEffect(() => {
    if (userWithPin) updateUserWithPin();
  }, [userWithPin]);

  return (
    <Page title="Esqueci a senha">
      {formik.isSubmitting && <Loading alpha />}

      <Alert />

      <form onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column">
          <Flex flexDirection="column" px={{ m: 30, d: 45 }} py={{ m: 30, d: 45 }}>
            <Typography type="type6" color="authLabelColor" fontWeight="bold">
              Esqueceu sua senha?
            </Typography>

            <Box mt={2}>
              <Typography lineHeight="1.2" type="type2" color="authLabelColor">
                Preencha abaixo seu e-mail para receber as instruções necessárias para criar uma nova senha.
              </Typography>
            </Box>

            {/* E-mail */}
            {/* <Flex flexDirection="column">
            <Box>
              <AuthInput
                name="email"
                type="email"
                onChange={formikHandleChange}
                onFocus={formikHandleFocus}
                onBlur={formikHandleBlur}
                value={formik.values.email}
                className={clsx({ hasError: formik.touched.email && formik.errors.email })}
                placeholder="E-mail"
                tabIndex={1}
              />

              {formik.touched.email && formik.errors.email ? (
                <HelperText type="error">{formik.errors.email}</HelperText>
              ) : null}
            </Box>
          </Flex> */}
            {/* /E-mail */}

            {/* login */}
            <Flex flexDirection="column" mt={{ m: 4 }}>
              <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                Login:
              </Typography>

              <Box position="relative" mt={2}>
                <AuthInput2
                  name="login"
                  type="text"
                  onChange={formikHandleChange}
                  onFocus={formikHandleFocus}
                  onBlur={formikHandleBlur}
                  value={formik.values.login}
                  data-haserror={formik.touched.login && formik.errors.login}
                  tabIndex={1}
                />
              </Box>

              {formik.touched.login && formik.errors.login ? <HelperText type="error">{formik.errors.login}</HelperText> : null}
            </Flex>
            {/* /login */}

            {withPin && (
              <>
                {/* login */}
                <Flex flexDirection="column" mt={{ m: 4 }}>
                  <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                    PIN:
                  </Typography>

                  <Box position="relative" mt={2}>
                    <AuthInput2
                      name="pin"
                      type="text"
                      onChange={formikHandleChange}
                      onFocus={formikHandleFocus}
                      onBlur={formikHandleBlur}
                      value={formik.values.pin}
                      data-haserror={formik.touched.pin && formik.errors.pin}
                      tabIndex={1}
                    />
                  </Box>

                  {formik.touched.pin && formik.errors.pin ? <HelperText type="error">{formik.errors.pin}</HelperText> : null}
                </Flex>
                {/* /pin */}
              </>
            )}
          </Flex>

          <Flex>
            <AuthButton type="submit">Enviar</AuthButton>
          </Flex>

          <Flex bg="authBackground" justifyContent="center" py={3}>
            <Link to={createUrl('login')}>
              <Typography className="btn-auth-external" color="authFloatLinkColor">
                Voltar para o login
              </Typography>
            </Link>
          </Flex>
        </Flex>
      </form>
    </Page>
  );
};

export default ForgotPasswordView;
